import { useLocation } from 'react-router-dom';
import { useFetchOauthAuthConfig } from '@seaters-app/data-access';
import { LOCATION_STATES, routes } from '@seaters-app/constants';
import {
  createRedirectInfoFromLocation,
  encodeOAuthState,
} from '../utils/oauth-state-utils';

/**
 * Custom hook for OAuth login with proper state preservation
 * Handles encoding redirect information into OAuth state
 */
export const useOAuthLogin = (provider: string) => {
  const { state: locationState } = useLocation();
  const { data: oauthConfig } = useFetchOauthAuthConfig(provider);

  const getOAuthLoginUrl = (): string | null => {
    if (!oauthConfig?.uri) {
      return null;
    }

    try {
      const url = new URL(oauthConfig.uri);
      const originalState = url.searchParams.get('state');

      // Create redirect info from current location state
      const redirectInfo = createRedirectInfoFromLocation(
        locationState,
        LOCATION_STATES,
        routes
      );

      // Encode the original OAuth state with redirect information
      const modifiedState = encodeOAuthState(originalState, redirectInfo);

      // Update the state parameter in the OAuth URL
      url.searchParams.set('state', modifiedState);

      return url.toString();
    } catch (error) {
      console.error('[OAuth] Error modifying OAuth URL:', error);
      return oauthConfig.uri;
    }
  };

  const redirectToOAuthLogin = () => {
    const loginUrl = getOAuthLoginUrl();
    if (loginUrl) {
      window.location.href = loginUrl;
    }
  };

  return {
    oauthConfig,
    locationState,
    getOAuthLoginUrl,
    redirectToOAuthLogin,
    isLoading: !oauthConfig,
  };
};
